import React, { useState, useMemo, useEffect, useRef } from 'react';
import { TransportType } from '../constants/entities';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  TextField,
  MenuItem,
  Stack,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Worker } from '../services/workerService';
import { Region } from '../services/regionService';
import { Settlement, listSettlements } from '../services/settlementService';
import { useQuery } from '@tanstack/react-query';
import WorkerScheduleEditor from './WorkerScheduleEditor';
// ;
import { updatePlanWorker, PlanWorkerCreate } from '../services/planService';
import dayjs from "dayjs";

interface WorkerPlanManagerProps {
  open: boolean;
  onClose: () => void;
  workers: Worker[];
  selectedWorkerIds: number[];
  onWorkersChange: (workerIds: number[]) => void;
  onPlanWorkersChange?: (planWorkers: PlanWorkerCreate[]) => void; // New prop for complete worker data
  title?: string;
  includeByDefault?: boolean;
  regions?: Region[];
  settlements?: Settlement[];
  selectedRId?: number | '';
  selectedSId?: number | '';
  onRegionChange?: (regionId: number | '') => void;
  onSettlementChange?: (settlementId: number | '') => void;
  planWorkers: any[];
  planId?: number;
  planMonth?: string;
}

const WorkerPlanManager: React.FC<WorkerPlanManagerProps> = ({
  open,
  onClose,
  workers,
  selectedWorkerIds,
  onWorkersChange,
  onPlanWorkersChange, // Add this prop to the component
  title = "Manage Workers",
  includeByDefault = true,
  regions = [],
  settlements = [], // keep for initial value
  selectedRId,
  selectedSId,
  onRegionChange,
  onSettlementChange,
  planWorkers,
  planId,
  planMonth
}) => {
  const [searchText, setSearchText] = useState('');
  const [transportFilter, setTransportFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState<number | ''>('');
  const [selectedSettlementId, setSelectedSettlementId] = useState<number | ''>('');
  const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [editingWorkerSchedule, setEditingWorkerSchedule] = useState<any>(null);
  const [editingWorkerPlanId, setEditingWorkerPlanId] = useState<number | null>(null);
  const [editingWorkerMonth, setEditingWorkerMonth] = useState<string>('');

  // Track if we've done initial setup for this dialog session
  const hasInitialized = useRef(false);

  // Function to create complete plan worker data
  const createPlanWorkerData = (workerIds: number[]): PlanWorkerCreate[] => {
    return workerIds.map(workerId => {
      const worker = workers.find(w => w.id === workerId);
      const existingPlanWorker = planWorkers.find((pw: any) => pw.worker_id === workerId);
      
      return {
        worker_id: workerId,
        availability_hours_override: existingPlanWorker?.availability_hours_override || worker?.availability_hours,
        pricing_override: existingPlanWorker?.pricing_override || worker?.pricing,
        work_time_override: existingPlanWorker?.work_time_override || undefined, // Not available in Worker interface
        default_timings_override: existingPlanWorker?.default_timings_override || undefined, // Not available in Worker interface
        availability_hours_modified: existingPlanWorker?.availability_hours_modified || false,
        pricing_modified: existingPlanWorker?.pricing_modified || false,
        work_time_modified: existingPlanWorker?.work_time_modified || false,
        default_timings_modified: existingPlanWorker?.default_timings_modified || false,
      };
    });
  };

  // Enhanced worker change handler that also calls onPlanWorkersChange
  const handleWorkersChange = (workerIds: number[]) => {
    onWorkersChange(workerIds);
    if (onPlanWorkersChange) {
      const planWorkersData = createPlanWorkerData(workerIds);
      onPlanWorkersChange(planWorkersData);
    }
  };

  // Fetch settlements for the selected region
  const { data: dynamicSettlements = settlements } = useQuery<Settlement[]>({
    queryKey: ['settlements', selectedRegionId],
    queryFn: () => selectedRegionId ? listSettlements(selectedRegionId) : Promise.resolve(settlements),
    enabled: !!selectedRegionId,
    // Use initial settlements for instant display
    initialData: settlements
  });

  // Filter settlements based on selected region (use dynamicSettlements)
  const filteredSettlements = useMemo(() => {
    if (!selectedRegionId) return dynamicSettlements;
    return dynamicSettlements.filter(settlement => settlement.region_id === selectedRegionId);
  }, [dynamicSettlements, selectedRegionId]);

  // Auto-select all workers by default when dialog opens and reset filters
  useEffect(() => {
    if (open && !hasInitialized.current) {
      // Reset filters when dialog opens
      setSearchText('');
      setTransportFilter('');
      setLocationFilter('');  
      setSelectedRegionId(selectedRId||'');
      setSelectedSettlementId(selectedSId||'');
      
      // Auto-select workers if enabled and this is the first time opening
      if (includeByDefault && selectedWorkerIds.length === 0) {
        const allWorkerIds = workers
          .map(w => w.id)
          .filter((id): id is number => id !== undefined && id !== null);
        handleWorkersChange(allWorkerIds);
      }
      
      // Mark as initialized for this dialog session
      hasInitialized.current = true;
    } else if (!open) {
      // Reset initialization flag when dialog closes
      hasInitialized.current = false;
    }
  }, [open, includeByDefault, selectedWorkerIds.length, workers, handleWorkersChange, selectedRId, selectedSId]);

  // Keep local region/settlement in sync with props if they change while dialog is open
  useEffect(() => {
    if (selectedRId !== undefined) {
      setSelectedRegionId(selectedRId);
    }
    if (selectedSId !== undefined) {
      setSelectedSettlementId(selectedSId);
    }
  }, [selectedRId, selectedSId]);

  // Filter workers
  const filteredWorkers = useMemo(() => {
    return workers.filter(worker => {
      const matchesSearch = !searchText || 
        worker.name.toLowerCase().includes(searchText.toLowerCase()) ||
        worker.email.toLowerCase().includes(searchText.toLowerCase());
      
      const matchesTransport = !transportFilter || worker.transport_type === transportFilter;
      
      const matchesLocation = !locationFilter || 
        worker.settlement?.name?.toLowerCase().includes(locationFilter.toLowerCase());

      // Filter by selected region/settlement  
      const matchesRegion = !selectedRegionId || worker.settlement?.region?.id === selectedRegionId;
      const matchesSettlement = !selectedSettlementId || worker.settlement?.id === selectedSettlementId;

      return matchesSearch && matchesTransport && matchesLocation && matchesRegion && matchesSettlement;
    });
  }, [workers, searchText, transportFilter, locationFilter, selectedRegionId, selectedSettlementId]);

  // Calculate statistics
  const selectedWorkers = useMemo(() => 
    workers.filter(w => w.id && selectedWorkerIds.includes(w.id)), 
    [workers, selectedWorkerIds]
  );

  const totalWorkingHours = useMemo(() => {
    return selectedWorkers.reduce((total, worker) => {
      try {
        const schedule = typeof worker.availability_hours === 'string' 
          ? JSON.parse(worker.availability_hours) 
          : worker.availability_hours;
        
        if (!schedule) return total;

        let weeklyHours = 0;
        Object.values(schedule).forEach((daySchedule: any) => {
          if (Array.isArray(daySchedule)) {
            daySchedule.forEach((period: any) => {
              weeklyHours += period.work_time || 0;
            });
          }
        });
        return total + weeklyHours;
      } catch {
        return total;
      }
    }, 0);
  }, [selectedWorkers]);

  const transportTypeCounts = useMemo(() => {
    const counts = { auto: 0, public_transport: 0, other: 0 };
    selectedWorkers.forEach(worker => {
      if (worker.transport_type === TransportType.AUTO) counts.auto++;
      else if (worker.transport_type === TransportType.PUBLIC_TRANSPORT) counts.public_transport++;
      else counts.other++;
    });
    return counts;
  }, [selectedWorkers]);

  const handleSelectAll = () => {
    const allFilteredIds = filteredWorkers
      .map(w => w.id)
      .filter((id): id is number => id !== undefined && id !== null);
    handleWorkersChange(Array.from(new Set([...selectedWorkerIds, ...allFilteredIds])));
  };

  const handleDeselectAll = () => {
    const filteredIds = new Set(
      filteredWorkers
        .map(w => w.id)
        .filter((id): id is number => id !== undefined && id !== null)
    );
    handleWorkersChange(selectedWorkerIds.filter(id => !filteredIds.has(id)));
  };

  const handleWorkerToggle = (workerId: number) => {
    if (!workerId) return; // Safety check for undefined/empty IDs
    
    if (selectedWorkerIds.includes(workerId)) {
      handleWorkersChange(selectedWorkerIds.filter(id => id !== workerId));
    } else {
      handleWorkersChange([...selectedWorkerIds, workerId]);
    }
  };

  // Handler to open schedule editor for a worker
  const handleEditSchedule = (worker: Worker) => {
    setEditingWorker(worker);
    // Find planWorker for this worker
    const planWorker = planWorkers.find((pw: any) => pw.worker_id === worker.id);
    let schedule = planWorker?.availability_hours_override || worker.availability_hours;
    if (typeof schedule === 'string') {
      try { schedule = JSON.parse(schedule); } catch { schedule = {}; }
    }
    setEditingWorkerSchedule(schedule);
    setEditingWorkerPlanId(planId || null);
    setEditingWorkerMonth(planMonth || dayjs().format('YYYY-MM'));
    setScheduleEditorOpen(true);
  };

  // Handler to save schedule override
  const handleSaveSchedule = async (schedule: any) => {
    if (!editingWorker) return;
    try {
      if (editingWorkerPlanId && editingWorker.id) {
        // Call API to save override for this worker/plan
        await updatePlanWorker(editingWorkerPlanId, editingWorker.id, { availability_hours_override: schedule });
      }
    } catch (_error) {
      console.error(_error);
    }
    setScheduleEditorOpen(false);
    setEditingWorker(null);
    setEditingWorkerSchedule(null);
    setEditingWorkerPlanId(null);
    setEditingWorkerMonth('');
  };

  const uniqueTransportTypes = Array.from(new Set(workers.map(w => w.transport_type)));
  // const uniqueLocations = Array.from(new Set(workers.map(w => w.settlement?.name).filter(Boolean)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {/* Global Statistics */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>Plan Overview</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Card variant="outlined" sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Typography variant="h4" color="primary">
                  {selectedWorkers.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Workers
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Typography variant="h4" color="secondary">
                  {Math.round(totalWorkingHours * 4.33)}h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monthly Hours
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Typography variant="h4" color="success.main">
                  {transportTypeCounts.auto}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Auto Workers
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Typography variant="h4" color="info.main">
                  {transportTypeCounts.public_transport}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Public Transport
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        {/* Filters */}
        <Box mb={2}>
          <Typography variant="subtitle1" gutterBottom>Filter Workers</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 250 }}
            />
            <TextField
              size="small"
              select
              label="Transport"
              value={transportFilter}
              onChange={(e) => setTransportFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Transport</MenuItem>
              {uniqueTransportTypes.map(type => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </TextField>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Region</InputLabel>
              <Select
                value={selectedRegionId}
                onChange={(e) => {
                  const newRegionId = e.target.value as number | '';
                  setSelectedRegionId(newRegionId);
                  setSelectedSettlementId(''); // Reset settlement when region changes
                  onRegionChange?.(newRegionId);                  
                  onSettlementChange?.(''); // Notify parent that settlement should be reset
                }}
                label="Region"
              >
                <MenuItem value="">All Regions</MenuItem>
                {regions.length === 0 ? (
                  <MenuItem disabled>No regions available</MenuItem>
                ) : (
                  regions.map(region => (
                    <MenuItem key={region.id} value={region.id}>{region.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }} disabled={!selectedRegionId}>
              <InputLabel>Settlement</InputLabel>
              <Select
                value={selectedSettlementId}
                onChange={(e) => {
                  const newSettlementId = e.target.value as number | '';
                  setSelectedSettlementId(newSettlementId);
                  onSettlementChange?.(newSettlementId); // Notify parent of settlement change
                }}
                label="Settlement"
              >
                <MenuItem value="">All Settlements</MenuItem>
                {!selectedRegionId ? (
                  <MenuItem disabled>Select a region first</MenuItem>
                ) : filteredSettlements.length === 0 ? (
                  <MenuItem disabled>No settlements available</MenuItem>
                ) : (
                  filteredSettlements.map(settlement => (
                    <MenuItem key={settlement.id} value={settlement.id}>{settlement.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Filter by location..."
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              sx={{ minWidth: 150 }}
            />
            <Button variant="outlined" onClick={handleSelectAll} size="small">
              Select All Filtered
            </Button>
            <Button variant="outlined" onClick={handleDeselectAll} size="small">
              Deselect All Filtered
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Showing {filteredWorkers.length} of {workers.length} workers
          </Typography>
        </Box>

        {/* Workers Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Include</TableCell>
                <TableCell>Worker</TableCell>
                <TableCell>Transport</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Weekly Hours</TableCell>
                <TableCell>Pricing</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWorkers.map((worker) => {
                const isSelected = worker.id ? selectedWorkerIds.includes(worker.id) : false;
                
                // Calculate weekly hours for this worker
                let weeklyHours = 0;
                try {
                  const schedule = typeof worker.availability_hours === 'string' 
                    ? JSON.parse(worker.availability_hours) 
                    : worker.availability_hours;
                  
                  if (schedule) {
                    Object.values(schedule).forEach((daySchedule: any) => {
                      if (Array.isArray(daySchedule)) {
                        daySchedule.forEach((period: any) => {
                          weeklyHours += period.work_time || 0;
                        });
                      }
                    });
                  }
                } catch {
                  // Default hours if parsing fails
                  weeklyHours = 40;
                }

                return (
                  <TableRow 
                    key={worker.id} 
                    hover 
                    selected={isSelected}
                    onClick={worker.id ? () => handleWorkerToggle(worker.id as number) : undefined}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSelected} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 1,
                            bgcolor: worker.transport_type === 'auto' ? 'primary.main' : 'secondary.main'
                          }}
                        >
                          {worker.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {worker.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {worker.email}
                          </Typography>
                          {/* Add Edit Schedule button for selected workers */}
                          {isSelected && (
                            <Button size="small" variant="outlined" sx={{ mt: 0.5 }} onClick={e => { e.stopPropagation(); handleEditSchedule(worker); }}>
                              Edit Schedule
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={worker.transport_type} 
                        size="small"
                        color={worker.transport_type === 'auto' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      {worker.settlement?.name || '-'}
                    </TableCell>
                    <TableCell>{weeklyHours}h</TableCell>
                    <TableCell>
                      {worker.pricing ? `$${worker.pricing}` : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onClose}>
          Apply ({selectedWorkers.length} workers)
        </Button>
      </DialogActions>
      {/* WorkerScheduleEditor Modal */}
      {scheduleEditorOpen && editingWorker && (
        <Dialog open={scheduleEditorOpen} onClose={() => setScheduleEditorOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Schedule for {editingWorker.name}</DialogTitle>
          <DialogContent>
            <WorkerScheduleEditor
              workerId={editingWorker.id ?? ''}
              planId={editingWorkerPlanId || ''}
              month={editingWorkerMonth}
              initialSchedule={editingWorkerSchedule || {}}
              onSave={handleSaveSchedule}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleEditorOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};

export default WorkerPlanManager; 